import React, { useCallback, useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { usePost, useToken } from "../API";
import { AuthContext } from "../Context";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { ChatConversationContext } from "./ChatContext";

export type ChatSessionContextType = {
  createdAt?: Date,
  isLoading: boolean,
  loaded: boolean,
  termsAccepted?: string,
  setTermsAccepted(termsHash: string): void,
  conversationId?: string,
  clearSession(): void
}

export const ChatSessionContext = React.createContext<ChatSessionContextType>({
  isLoading: false,
  loaded: false,
  setTermsAccepted() { console.warn('no chat session present, ignoring setTermsAccepted()') },
  clearSession() { console.warn('no chat session present, ignoring clearSession()') },
});

export const ChatSessionProvider: React.FC<{ context: ChatSessionContextType, children: React.ReactNode }> = ({ context, children }) =>
  <ChatSessionContext.Provider value={context}>
    {children}
  </ChatSessionContext.Provider>;

/**
 * Put within any existing ChatContext, to enable persistence of the chat.
 */
export function DefaultChatSessionProvider({ children, conversationIdKey }: { children: React.ReactNode, conversationIdKey?: string }) {
  const conversationIdCookieName = conversationIdKey || "ai_coach_conversation_id";
  const getOrCreateConversation = usePost('/coach/conversation/create', { suppressErrorDisplay: true });
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [createdAt, setCreatedAt] = useState(new Date());
  const [termsAccepted, setTermsAccepted] = useState<string | undefined>();
  const [cookies, setCookie, deleteCookie] = useCookies([conversationIdCookieName]);
  const id = cookies[conversationIdCookieName];
  const { dispatch } = useContext(ChatConversationContext);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const authContext = useContext(AuthContext);
  const [, token] = useToken();

  const clearSession = useCallback(() => {
    deleteCookie(conversationIdCookieName);
    setConversationId(undefined);
    setLoaded(false);
  }, []);

  // load chat messages
  useAsyncEffect(async (cancel) => {
    if (!loaded && (authContext?.token?.() || token)) {
      const storedConvoId = cookies[conversationIdCookieName];
      setIsLoading(true);
      const existing = await getOrCreateConversation({ previousConvoId: storedConvoId ?? undefined }, cancel);
      if (existing && !("error" in existing)) {
        if (existing.conversationId) {
          setCookie(conversationIdCookieName, existing.conversationId)
        }

        setConversationId(existing.conversationId);

        setCreatedAt(new Date(existing.createdAt));

        for (const message of existing.items) {
          if(message.type === 'error') continue;
          if(message.type === 'appState') continue;
          dispatch(message);
        }

        setLoaded(true);
      }
      setIsLoading(false);
    }
  }, [id, loaded, getOrCreateConversation, setConversationId, authContext?.token?.(), token]);

  return <ChatSessionProvider context={{
    createdAt,
    isLoading,
    loaded,
    termsAccepted,
    setTermsAccepted,
    conversationId,
    clearSession
  }}>
    {children}
  </ChatSessionProvider>
}
